import { RouterSelectors } from '@next/core-lib/router-store';
import { createSelector, createFeatureSelector } from '@ngrx/store';

import { adapter } from './contactperson.reducer';
import {
  Application,
  ContactpersonModuleState,
  AccessRule,
  NewRole,
} from '../../contactperson/models/contactperson.model';
import { Customer } from '../../shared/models/shared.model';

const { selectAll } = adapter.getSelectors();

export const getContactpersonModuleState =
  createFeatureSelector<ContactpersonModuleState>('contactpersonModule');

export const getContactpersonState = createSelector(
  getContactpersonModuleState,
  (state) => state.contactpersons,
);

export const getSelectedContactpersonId = createSelector(
  RouterSelectors.getRouteParam('contactpersonId'),
  (contactpersonId) => (contactpersonId ? parseInt(contactpersonId, 10) : undefined),
);

export const getSelectedContactperson = createSelector(
  getContactpersonModuleState,
  (state) => state.selectedContactperson,
);

export const getAllContactpersons = createSelector(getContactpersonState, selectAll);

export const selectLoadingCustomersForSearchbox = createSelector(
  getContactpersonModuleState,
  (state) => state.loadingCustomersforSearchbox,
);

export const getAllCustomersforSearchbox = createSelector(getContactpersonModuleState, (state) =>
  state.customersForSearchbox.map((customer: Customer) => ({
    id: customer.id,
    name: `${customer.name} (${customer.emailAddress})`,
  })),
);

export const selectLoadingContactpersons = createSelector(
  getContactpersonModuleState,
  (state) => state.loadingContactpersons,
);

export const selectProfileId = createSelector(
  getContactpersonModuleState,
  (state) => state.profileId,
);

export const selectApplications = createSelector(
  getContactpersonModuleState,
  (state) => state.applications || [],
);
export const selectApplicationGroups = createSelector(
  getContactpersonModuleState,
  (state) => state.applicationGroups || [],
);
export const selectRoles = createSelector(
  getContactpersonModuleState,
  (state) => state.roles || [],
);
export const selectAccessRules = createSelector(
  getContactpersonModuleState,
  (state) => state.accessRules || [],
);

export const selectSelectedApplicationGroups = createSelector(
  selectApplications,
  selectApplicationGroups,
  selectAccessRules,
  (applications, applicationGroups, accessRules) =>
    applicationGroups.filter((applicationGroup) =>
      applications
        .filter((application) => application.applicationGroupId === applicationGroup.id)
        .reduce(
          (include, application) =>
            include ||
            accessRules.filter((accessRule) => accessRule.applicationKey === application.key)
              .length > 0,
          false,
        ),
    ),
);

export const selectUnselectedApplicationGroups = createSelector(
  selectSelectedApplicationGroups,
  selectApplicationGroups,
  selectApplications,
  (selectedApplicationGroups, applicationGroups, applications) =>
    applicationGroups.filter(
      (applicationGroup) =>
        !selectedApplicationGroups.find(
          (selectedApplicationGroup) => selectedApplicationGroup.id === applicationGroup.id,
        ) &&
        applications.filter((application) => application.applicationGroupId === applicationGroup.id)
          .length > 0,
    ),
);

export const hasUnselectedApplicationGroups = createSelector(
  selectUnselectedApplicationGroups,
  (applicationGroups) => applicationGroups && applicationGroups.length > 0,
);

export const selectApplicationsForGroup = createSelector(
  selectApplications,
  (applications: Application[], groupId: number) =>
    applications.filter((application: Application) => application.applicationGroupId === groupId),
);

export const selectAccessRulesForGroup = createSelector(
  selectAccessRules,
  selectApplicationsForGroup,
  (accessRules, applications, _: number) => {
    const applicationKeys = applications.map((application) => application.key);
    return accessRules.filter(
      (accessRule) => applicationKeys.indexOf(accessRule.applicationKey) !== -1,
    );
  },
);

export const selectSelectedRole = createSelector(
  selectAccessRules,
  selectRoles,
  (accessRules: AccessRule[], roles: NewRole[], key: string) => {
    const accessRule = accessRules.find((rule) => rule.applicationKey === key);
    const accessRole = accessRule
      ? roles.find((role) => accessRule.roleKey === role.key)
      : undefined;
    return accessRole ? accessRole.key : undefined;
  },
);

export const getCustomersWithRelations = createSelector(
  getContactpersonModuleState,
  (state) => state.customersWithRelations,
);

export const getCustomerRelationsLocations = createSelector(
  getContactpersonModuleState,
  (state) => state.customerRelationsLocations,
);

export const hasLocationWithoutRelation = createSelector(getContactpersonModuleState, (state) =>
  state.customerRelationsLocations.filter((l) => !l.contactPersonHasRelation),
);

export const getCustomerRelationsAllLocationIds = createSelector(
  getContactpersonModuleState,
  (state) =>
    state.customerRelationsLocations
      ? state.customerRelationsLocations.map((location) => location.id)
      : undefined,
);

export const getCustomerLocationIdsWithRelation = createSelector(
  getContactpersonModuleState,
  (state) =>
    state.customerRelationsLocations
      ? state.customerRelationsLocations
          .filter((location) => location.contactPersonHasRelation)
          .map((location) => location.id)
      : undefined,
);

export const getSearchText = createSelector(
  getContactpersonModuleState,
  (state) => state.searchText,
);
